import { scaleOrdinal } from '@visx/scale';
import { ParentSize } from '@visx/responsive';
import { useMemo, useRef } from 'react';
import classNames from 'classnames';
import { isArray } from 'lodash';

import { notNil } from '@/lib/utils';
import { NoMatchBanner } from '@/components/banner';
import { DropdownMenuItem } from '@/components/dropdown';
import { Grouping } from '@/explore/types';
import { GroupFilter } from '@/explore/utils/drilldown';

import { CategoryData, GroupedChartData } from '../grouped-chart/types';
import { Legend } from '../legend';
import { GroupedChart } from '../grouped-chart';
import { chartColors } from '../utils';
import {
  getChartCount,
  getLeftRightAxisTypes,
  getSeries,
  getValueKeys,
} from '../grouped-chart/utils';

import { HorizontalBarChart } from './horizontal-bar-chart';
import { StackedHorizontalBarChart } from './stacked-horizontal-bar-chart';
import { getLegendData } from '../legend/utils';
import { alignScale, getValueScale } from '../grouped-chart/utils/scale';

import commonStyles, {
  barColor,
  minChartHeight,
  multiChartChartHeight,
} from '../charts.module.scss';
import styles from './grouped-horizontal-chart.module.scss';

export const NumXTicks = 7;
const ValuePaddingCoef = 0.1;
const ChartHeight = parseInt(multiChartChartHeight);
const MinChartHeight = parseInt(minChartHeight);
const WidthPerGroup = parseInt(commonStyles.widthPerGroup);
const MinChartWidth = 50;

type GroupedHorizontalBarChartProps = {
  data: GroupedChartData<CategoryData>;
  grouping: Grouping[];
  stacked?: boolean;
  showTotals: boolean;
  isResized?: boolean;
  isTableVisible?: boolean;
  isNotFullData?: boolean;
  onCategoryClick?: (params: {
    x: number;
    y: number;
    key: string;
    /**
     * The filters that are applied to the group. Also includes filter from combined key if exists.
     */
    groupFilters: GroupFilter[];
  }) => void;
  buildSeriesMenu: (seriesKey: string) => DropdownMenuItem[];
};

export const GroupedHorizontalBarChart = ({
  data,
  grouping,
  stacked = false,
  showTotals,
  isResized = false,
  isTableVisible = true,
  isNotFullData = false,
  onCategoryClick,
  buildSeriesMenu,
}: GroupedHorizontalBarChartProps) => {
  const groupCount = grouping.length;
  const isMultiChart = groupCount >= 3; // 1st = x axis, 2nd = multiple lines, 3rd, ... = multiple charts
  const customHeightEnabled = !isTableVisible && isResized;
  const chartCount = getChartCount(data);

  return (
    <ParentSize
      debounceTime={0}
      className={classNames(commonStyles.chartContainer, {
        [commonStyles.customHeight]: customHeightEnabled,
      })}
      style={{
        minHeight:
          isMultiChart && !customHeightEnabled ? `${ChartHeight * chartCount}px` : undefined,
      }}>
      {(parent) => {
        return (
          <GroupedHorizontalBarChartInner
            data={data}
            grouping={grouping}
            width={parent.width}
            height={parent.height}
            showTotals={showTotals}
            stacked={stacked}
            isNotFullData={isNotFullData}
            onCategoryClick={onCategoryClick}
            buildSeriesMenu={buildSeriesMenu}
          />
        );
      }}
    </ParentSize>
  );
};

type GroupedHorizontalBarChartInnerProps = GroupedHorizontalBarChartProps & {
  width: number;
  height: number;
};

// Sets all series chartType to 'bar'
const overrideChartType = (
  data: GroupedChartData<CategoryData>,
): GroupedChartData<CategoryData> => {
  if (isArray(data)) {
    return data.map((item) => ({
      ...item,
      items: overrideChartType(item.items),
    }));
  }
  return {
    ...data,
    chartData: {
      ...data.chartData,
      series: data.chartData.series.map((seriesItem) => ({
        ...seriesItem,
        chartType: 'bar' as const,
      })),
    },
  };
};

const GroupedHorizontalBarChartInner = (props: GroupedHorizontalBarChartInnerProps) => {
  const {
    width,
    height,
    grouping,
    showTotals,
    stacked = false,
    isNotFullData = false,
    onCategoryClick,
    buildSeriesMenu,
  } = props;

  // Override chart type to 'bar' for all series as we do not support other types in horizontal bar chart
  const data = useMemo(() => overrideChartType(props.data), [props.data]);

  const seriesDomain = getValueKeys(data);
  const footerRef = useRef<HTMLDivElement>(null);

  const userDefinedColors = getSeries(data)
    .map(({ color }) => color)
    .filter(notNil);
  const colorScale = scaleOrdinal<string, string>({
    domain: seriesDomain,
    range:
      seriesDomain.length === 1 && userDefinedColors.length === 0
        ? [barColor]
        : [...userDefinedColors, ...chartColors],
  });

  const axisTypes = getLeftRightAxisTypes(getSeries(data));
  const groupsWidth = WidthPerGroup * Math.max(1, grouping.length - 1);
  const chartWidth = Math.max(MinChartWidth, width - groupsWidth);

  const bottomValueScale = useMemo(
    () =>
      getValueScale(
        data,
        stacked,
        'left',
        ValuePaddingCoef,
        [0, chartWidth],
        NumXTicks,
        axisTypes.left === 'Integer' ? 1 : undefined,
      ),
    [axisTypes.left, chartWidth, data, stacked],
  );
  const topValueScale = useMemo(
    () =>
      alignScale(
        getValueScale(
          data,
          stacked,
          'right',
          ValuePaddingCoef,
          [0, chartWidth],
          NumXTicks,
          axisTypes.right === 'Integer' ? 1 : undefined,
        ),
        bottomValueScale,
        NumXTicks,
        axisTypes.right === 'Integer' ? 1 : undefined,
      ),
    [axisTypes.right, chartWidth, data, bottomValueScale, stacked],
  );

  const valueScales = useMemo(
    () => ({
      left: bottomValueScale,
      right: topValueScale,
    }),
    [bottomValueScale, topValueScale],
  );

  // eslint-disable-next-line react-compiler/react-compiler
  const footerHeight = footerRef?.current?.clientHeight ?? 75;

  const chartCount = getChartCount(data);
  // eslint-disable-next-line react-compiler/react-compiler
  const chartHeight = Math.max(MinChartHeight, Math.floor((height - footerHeight) / chartCount));

  const isPartialWarningShown = isNotFullData && !isArray(data);

  return (
    <div>
      <GroupedChart
        width={width}
        widthPerGroup={WidthPerGroup}
        data={data}
        renderChart={(chartData, width, groupFilters) => {
          if (chartData.items.length === 0) {
            return <NoMatchBanner />;
          }

          const isStacked = stacked && chartData.series.length > 1;

          return isStacked ? (
            <StackedHorizontalBarChart
              data={chartData}
              width={width}
              height={chartHeight}
              categoryFormat={{ precision: 'month' }}
              colorScale={colorScale}
              valueScales={valueScales}
              showTotals={showTotals}
              onCategoryClick={(input) =>
                onCategoryClick?.({
                  ...input,
                  groupFilters: [...groupFilters, ...input.groupFilters],
                })
              }
            />
          ) : (
            <HorizontalBarChart
              data={chartData}
              width={width}
              height={chartHeight}
              categoryFormat={{ precision: 'month' }}
              colorScale={colorScale}
              valueScales={valueScales}
              onCategoryClick={(input) =>
                onCategoryClick?.({
                  ...input,
                  groupFilters: [...groupFilters, ...input.groupFilters],
                })
              }
            />
          );
        }}
      />
      <div className={styles.footer} ref={footerRef}>
        <div className={styles.partialDataWarning}>
          {isPartialWarningShown ? `Showing first ${data.chartData.items.length}` : null}
        </div>
        <Legend data={getLegendData(data)} buildSeriesMenu={buildSeriesMenu} />
      </div>
    </div>
  );
};
